import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import IncidentForm from './components/IncidentForm';
import FormProgress from './components/FormProgress';
import QuickActions from './components/QuickActions';
import Icon from '../../components/AppIcon';

const CreateIncident = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSections, setCompletedSections] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);

  // Mock user data
  const mockUser = {
    id: 'user_001',
    name: 'Sarah Wilson',
    email: 'sarah.wilson@cyberguard.gov',
    role: 'Senior Analyst',
    permissions: ['create_incident', 'view_incidents', 'assign_incidents']
  };

  // Mock incident counts for header
  const mockIncidentCounts = {
    critical: 3,
    high: 8,
    medium: 15,
    low: 12
  };

  // Mock recent incidents for quick actions
  const mockRecentIncidents = [
    {
      id: 'INC-2024-0892',
      title: 'Suspicious Email Campaign Detected',
      category: 'phishing',
      severity: 'medium',
      createdAt: '2 hours ago'
    },
    {
      id: 'INC-2024-0891',
      title: 'Malware Detection on Workstation',
      category: 'malware',
      severity: 'high',
      createdAt: '4 hours ago'
    },
    {
      id: 'INC-2024-0890',
      title: 'Unauthorized Access Attempt',
      category: 'unauthorized_access',
      severity: 'high',
      createdAt: '6 hours ago'
    }
  ];

  // Auto-save draft functionality
  useEffect(() => {
    const savedDraft = localStorage.getItem('incident_draft');
    if (savedDraft) {
      const draftData = JSON.parse(savedDraft);
      setLastSaved(new Date(draftData.savedAt));
    }
  }, []);

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    
    try {
        await fetch(`${import.meta.env.VITE_APP_SERVER_URL}/incident`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });


        localStorage.removeItem('incident_draft');

        navigate('/incidents-list', {replace: true});
    } catch (error) {
      console.error('Error creating incident:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async (formData) => {
    setIsDraftSaving(true);
    
    try {
      // Save to localStorage (in real app, save to backend)
      const draftData = {
        ...formData,
        savedAt: new Date()?.toISOString(),
        userId: mockUser?.id
      };
      
      localStorage.setItem('incident_draft', JSON.stringify(draftData));
      setLastSaved(new Date());
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Draft saved successfully');
      
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsDraftSaving(false);
    }
  };

  const handleTemplateSelect = (template) => {
    console.log('Template selected:', template);
    setShowQuickActions(false);
    
    // In real app, populate form with template data
    if (template?.id === 'similar' && template?.data) {
      // Populate form with similar incident data
      console.log('Using similar incident as template:', template?.data);
    } else {
      // Populate form with predefined template
      console.log('Using predefined template:', template);
    }
  };

  const handleImportData = (source) => {
    console.log('Importing data from:', source);
    setShowQuickActions(false);
    
    // In real app, handle different import sources
    switch (source) {
      case 'email': console.log('Importing from email...');
        break;
      case 'alert': console.log('Importing from alert system...');
        break;
      case 'csv': console.log('Importing from CSV file...');
        break;
      default:
        break;
    }
  };

  const updateFormProgress = (sectionId, isCompleted) => {
    if (isCompleted && !completedSections?.includes(sectionId)) {
      setCompletedSections(prev => [...prev, sectionId]);
    } else if (!isCompleted && completedSections?.includes(sectionId)) {
      setCompletedSections(prev => prev?.filter(id => id !== sectionId));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} incidentCounts={mockIncidentCounts} />
      <main className="nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Create New Incident</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Report and document cybersecurity incidents with comprehensive details
                </p>
              </div>
              
              <div className="mt-4 sm:mt-0 flex items-center space-x-4">
                {lastSaved && (
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Save" size={16} />
                    <span>Last saved: {lastSaved?.toLocaleTimeString()}</span>
                  </div>
                )}
                
                <button
                  onClick={() => setShowQuickActions(!showQuickActions)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-foreground bg-muted rounded-md hover:bg-muted/80 transition-colors duration-200"
                >
                  <Icon name={showQuickActions ? "EyeOff" : "Eye"} size={16} />
                  <span>{showQuickActions ? 'Hide' : 'Show'} Quick Actions</span>
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <FormProgress 
                currentStep={currentStep}
                totalSteps={5}
                completedSections={completedSections}
              />
              
              <IncidentForm
                onSubmit={handleSubmit}
                onSaveDraft={handleSaveDraft}
                isSubmitting={isSubmitting}
                isDraftSaving={isDraftSaving}
                onProgressUpdate={updateFormProgress}
              />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {showQuickActions && (
                <QuickActions
                  onTemplateSelect={handleTemplateSelect}
                  onImportData={handleImportData}
                  recentIncidents={mockRecentIncidents}
                />
              )}

              {/* Guidelines Card */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="BookOpen" size={20} className="mr-2" />
                  Reporting Guidelines
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Required Information</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Clear, descriptive incident title</li>
                      <li>• Detailed description (minimum 50 characters)</li>
                      <li>• Appropriate severity and priority levels</li>
                      <li>• All affected systems and services</li>
                      <li>• Estimated impact and user count</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Best Practices</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Include timestamps for all events</li>
                      <li>• Attach relevant evidence files</li>
                      <li>• Use appropriate tags for categorization</li>
                      <li>• Document initial response actions</li>
                      <li>• Assign to appropriate team member</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Severity Levels</h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-error rounded-full"></div>
                        <span className="text-xs text-muted-foreground">Critical: System-wide outage</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-warning rounded-full"></div>
                        <span className="text-xs text-muted-foreground">High: Significant impact</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-accent rounded-full"></div>
                        <span className="text-xs text-muted-foreground">Medium: Moderate impact</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-success rounded-full"></div>
                        <span className="text-xs text-muted-foreground">Low: Minor disruption</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contacts */}
              <div className="bg-card rounded-lg border border-border p-6 mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Phone" size={20} className="mr-2" />
                  Emergency Contacts
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">SOC Hotline</p>
                      <p className="text-xs text-muted-foreground">24/7 Emergency Response</p>
                    </div>
                    <a 
                      href="tel:+1-800-SOC-HELP"
                      className="text-sm font-medium text-primary hover:text-primary/80"
                    >
                      (800) 762-4357
                    </a>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Incident Commander</p>
                      <p className="text-xs text-muted-foreground">Critical Incidents Only</p>
                    </div>
                    <a 
                      href="tel:+1-800-CRITICAL"
                      className="text-sm font-medium text-primary hover:text-primary/80"
                    >
                      (800) 274-8422
                    </a>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">IT Support</p>
                      <p className="text-xs text-muted-foreground">Technical Assistance</p>
                    </div>
                    <a 
                      href="tel:+1-800-IT-HELP"
                      className="text-sm font-medium text-primary hover:text-primary/80"
                    >
                      (800) 484-3577
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateIncident;